import { useMemo, useState } from 'react';
import { db } from '@/lib/db';
import type { GroupRoundEnriched, Member } from '@/lib/db/types';
import { buildRoundPlayers, stakesFromGames } from '@/lib/games/engine/compute-round';
import { appliesMoney, stakeFromDbRow } from '@/lib/games/engine/stakes-helpers';
import { isMatchFormat } from '@/lib/games/engine/registry';
import {
  createPress,
  evaluateAllPressEligibilities,
  parsePresses,
  resolveMatchSides,
  type PressEligibility,
} from '@/lib/games/engine/presses';
import type { GameInstanceConfig, RoundContext } from '@/lib/games/engine/types';
import { GAME_LABELS } from '@/lib/games/game-catalog';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';

interface Props {
  round: GroupRoundEnriched;
  member: Member;
  holes: Array<{ hole_num: number; par: number }>;
  scoresByKey: Record<string, (number | null)[]>;
  teams?: string[][];
  currentHoleIdx: number;
  canEdit: boolean;
  onUpdated: () => void;
}

export function MatchPressControls({
  round,
  member,
  holes,
  scoresByKey,
  teams,
  currentHoleIdx,
  canEdit,
  onUpdated,
}: Props) {
  const [pending, setPending] = useState<{ formatId: string; eligibility: PressEligibility } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const myPlayerKey = round.players?.find((p) => p.member_id === member.id)?.player_key;

  const matchGames = useMemo(
    () => (round.games || []).filter((g) => g.enabled && isMatchFormat(g.format_id)),
    [round.games],
  );

  const ctx: RoundContext | null = useMemo(() => {
    if (!holes.length || !round.players?.length) return null;
    return {
      holes: holes as RoundContext['holes'],
      players: buildRoundPlayers(round.players, member.id),
      scores: scoresByKey,
      stakes: stakesFromGames(round.games || []),
      teams,
    };
  }, [holes, round.players, member.id, scoresByKey, round.games, teams]);

  const eligibilityByGame = useMemo(() => {
    if (!ctx || !myPlayerKey) return [];
    return matchGames.flatMap((g) => {
      const config = (g.config || {}) as GameInstanceConfig;
      const stake = stakeFromDbRow(g.format_id, (g.stakes || {}) as Record<string, unknown>);
      if (!appliesMoney(stake)) return [];
      return evaluateAllPressEligibilities({
        formatId: g.format_id,
        game: g,
        ctx,
        config,
        currentHoleIdx,
        playerKey: myPlayerKey,
      })
        .filter((e) => e.canPress)
        .map((eligibility) => ({ formatId: g.format_id, eligibility }));
    });
  }, [matchGames, ctx, myPlayerKey, currentHoleIdx]);

  const allPresses = useMemo(() => {
    return matchGames.flatMap((g) =>
      parsePresses((g.config || {}) as Record<string, unknown>).map((p) => ({
        ...p,
        formatId: g.format_id,
      })),
    );
  }, [matchGames]);

  if (!matchGames.length || !ctx) return null;

  const confirmPress = async () => {
    if (!pending?.eligibility.canPress || !myPlayerKey) return;
    const { formatId, eligibility } = pending;
    const game = matchGames.find((g) => g.format_id === formatId);
    if (!game) return;
    const config = (game.config || {}) as GameInstanceConfig;
    const sides = resolveMatchSides(formatId, ctx, config, eligibility.matchId);
    if (!sides || !eligibility.segment || !eligibility.initiatingSide) return;

    setBusy(true);
    setError(null);
    try {
      const press = createPress({
        formatId,
        segment: eligibility.segment,
        startHole: eligibility.startHole,
        endHole: eligibility.endHole,
        stakeCents: eligibility.stakeCents,
        initiatingSide: eligibility.initiatingSide,
        parentBetId: eligibility.parentBetId,
        sides,
        matchId: eligibility.matchId,
      });
      await db.groupRounds.appendPress(round.id, formatId, press);
      setPending(null);
      onUpdated();
    } catch (err) {
      setError((err as Error).message || 'Could not start press');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {canEdit && eligibilityByGame.map(({ formatId, eligibility }) => (
        <button
          key={`${formatId}-${eligibility.matchId || 'default'}-${eligibility.segment}`}
          type="button"
          onClick={() => setPending({ formatId, eligibility })}
          className="w-full rounded-lg border-2 border-gold bg-gold/10 py-2.5 text-sm font-bold text-navy"
        >
          Press — {GAME_LABELS[formatId] || formatId}
          {eligibility.matchId ? ` (match)` : ''}
          {' '}
          ({eligibility.segment}, ${(eligibility.stakeCents / 100).toFixed(0)})
        </button>
      ))}

      {allPresses.length > 0 && (
        <div className="rounded-xl border border-line bg-paper p-3">
          <div className="mb-2 text-[10px] font-extrabold tracking-wide text-gold-deep">PRESSES</div>
          <div className="space-y-2">
            {allPresses.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 text-xs">
                <div className="min-w-0">
                  <div className="font-semibold text-ink">
                    {GAME_LABELS[p.formatId] || p.formatId} · {p.segment}
                  </div>
                  <div className="text-mute">
                    Holes {p.startHole}–{p.endHole} · ${(p.stakeCents / 100).toFixed(0)}
                  </div>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                  p.status === 'open' ? 'bg-gold/20 text-gold-deep'
                    : p.status === 'won' ? 'bg-good/15 text-good'
                      : p.status === 'lost' ? 'bg-bad/10 text-bad'
                        : 'bg-line text-mute'
                }`}>
                  {p.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-bad/30 bg-bad/5 px-3 py-2 text-xs font-semibold text-bad">
          {error}
        </div>
      )}

      <ConfirmDialog
        open={pending != null}
        title="Start a press?"
        message={
          <div className="whitespace-pre-line text-sm leading-relaxed text-ink-soft">
            {'This adds a new bet for the remaining holes of this segment, at the same stake.\n\n'
            + 'Presses are a handshake agreement between players. You\'re responsible for confirming '
            + 'this press with the other side before you play on. Bag Tag tracks the bet but does not '
            + 'enforce or settle real money.'}
          </div>
        }
        confirmLabel="Start Press"
        cancelLabel="Cancel"
        busy={busy}
        onConfirm={() => void confirmPress()}
        onCancel={() => setPending(null)}
      />
    </>
  );
}
